import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { apiClient } from 'lib/api';
import type { QueryConfig } from 'lib/react-query';
import { DEV_MODE } from '../constants';
import queryKeys from './queryKeys';
import {
  filterMockByFlowNames,
  filterMockByQuery,
  mockTwitterCreativeSettings,
  sortTwitterCreativeSettings,
} from './mockTwitterCreativeSettings';
import type { GetTwitterCreativeSettingsParams, TwitterCreativeSettings } from './types';

const MOCK_DELAY_MS = 300;

const getMockTwitterCreativeSettings = async (
  params: GetTwitterCreativeSettingsParams,
): Promise<TwitterCreativeSettings[]> => {
  await new Promise((resolve) => setTimeout(resolve, MOCK_DELAY_MS));

  const filtered = filterMockByQuery(filterMockByFlowNames(mockTwitterCreativeSettings, params.flow), params.query);
  const sorted = sortTwitterCreativeSettings(filtered, params.sort, params.order);

  return sorted.map((item) => ({ ...item }));
};

/** Array params (`query`, `flow`, `sort`, `order`) are sent comma-joined by the shared serializer of `apiClient`. */
export const getTwitterCreativeSettings = async (
  { params }: { params: GetTwitterCreativeSettingsParams },
  signal?: AbortSignal,
): Promise<TwitterCreativeSettings[]> => {
  // PROTOTYPE ONLY: mock branch, active while DEV_MODE is true.
  if (DEV_MODE) {
    return getMockTwitterCreativeSettings(params);
  }

  const res = await apiClient.get<TwitterCreativeSettings[]>(queryKeys.creativeSettings, { params, signal });

  if (isAxiosError(res)) {
    const error = new Error('Get X Creative Settings Failed');
    error.cause = res.response?.data;
    return Promise.reject(error);
  }

  return res.data;
};

interface UseTwitterCreativeSettingsOptions<TData> {
  params: GetTwitterCreativeSettingsParams;
  config?: QueryConfig<typeof getTwitterCreativeSettings, TData>;
}

export const useTwitterCreativeSettings = <TData = TwitterCreativeSettings[]>({
  params,
  config,
}: UseTwitterCreativeSettingsOptions<TData>) =>
  useQuery({
    staleTime: 0,
    // Keeps the rows on screen while a new sort order loads.
    placeholderData: keepPreviousData,
    ...config,
    queryFn: ({ signal }) => getTwitterCreativeSettings({ params }, signal),
    queryKey: [queryKeys.creativeSettings, params],
  });
